import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { FormModal } from "@/components/pawkt/FormModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Phone, PawPrint, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { CUSTOMER_TYPES, CUSTOMER_SOURCES } from "@/lib/catalog";
import { PetsRepeater } from "@/components/pawkt/PetsRepeater";
import { mobileError, normalizeMobile, petDateErrors } from "@/lib/phone";
import { AddressPicker, addressError } from "@/components/pawkt/AddressPicker";
import { petHealthError } from "@/components/pawkt/VaccinationRecord";
import { petAgeError } from "@/lib/petAge";

const newIdemKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `idem_${Date.now()}_${Math.random()}`);

/* Shared, single-source customer (+ pets) creation flow. Reuses the canonical
   /customers(+/with-pets) API, duplicate intelligence, idempotency-key double-submit
   guard and validation. Used by the Customers page AND the Booking quick-create. */
export function CustomerCreateModal({ open, onOpenChange, onCreated,
                                      title = "Add Customer", submitLabel = "Create customer",
                                      testid = "customer-modal" }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [dup, setDup] = useState(null); // { candidates, body }
  const [idemKey, setIdemKey] = useState(newIdemKey);

  useEffect(() => { if (open) { setIdemKey(newIdemKey()); setDup(null); } }, [open]);

  const add = useMutation({
    mutationFn: ({ body, allowDup }) => {
      const { pets, ...customer } = body;
      const path = pets && pets.length ? "/customers/with-pets" : "/customers";
      const payload = pets && pets.length ? { customer, pets } : customer;
      return api.post(path, payload, {
        params: allowDup ? { allow_duplicate: true } : {},
        headers: { "Idempotency-Key": idemKey },
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["customers-lite"] });
      qc.invalidateQueries({ queryKey: ["customer-stats"] });
      setDup(null);
      onOpenChange(false);
      toast.success("Customer added");
      onCreated?.(res.data);
    },
    onError: (e, vars) => {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "CUSTOMER_DUPLICATE_CANDIDATE") {
        setDup({ candidates: d.candidates || [], body: vars.body });
      } else { toast.error(apiErr(e)); }
    },
  });

  const fields = [
    { name: "name", label: "Full name", placeholder: "Rahul Sharma", full: true },
    { name: "mobile", label: "Mobile", placeholder: "98XXXXXXXX", inputMode: "tel", validate: (x) => mobileError(x),
      hint: "10 digits. +91 or a leading 0 is accepted and removed." },
    { name: "email", label: "Email", placeholder: "rahul@email.com" },
    { name: "customer_type", label: "Type", type: "select", options: CUSTOMER_TYPES },
    { name: "source", label: "Source", type: "select", options: CUSTOMER_SOURCES },
    { name: "address", label: "Address (house / street)", full: true },
    { name: "location", type: "custom", full: true, validate: (_x, v) => addressError(v),
      render: (v, set) => <AddressPicker value={v} testid="cust-addr" onChange={(p) => Object.entries(p).forEach(([k, x]) => set(k, x))} error={v.pin !== undefined ? addressError(v) : ""} /> },
    { name: "notes", label: "Notes", type: "textarea", full: true },
    { name: "pets", type: "custom", full: true,
      render: (v, set) => <PetsRepeater value={v.pets || []} onChange={(arr) => set("pets", arr)} />,
      validate: (pets) => (pets || []).map((p) => Object.values(petDateErrors(p))[0] || petAgeError(p) || petHealthError(p)).find(Boolean) || "" },
  ];

  return (
    <>
      <FormModal open={open} onOpenChange={onOpenChange} title={title} testid={testid}
        fields={fields} initial={{ customer_type: "regular", source: "walk_in" }}
        submitLabel={submitLabel} onSubmit={(v) => add.mutateAsync({ body: { ...v, mobile: normalizeMobile(v.mobile) }, allowDup: false })} />

      <DuplicateDialog dup={dup} onClose={() => setDup(null)} navigate={navigate}
        onContinue={() => dup && add.mutate({ body: dup.body, allowDup: true })}
        busy={add.isPending} />
    </>
  );
}

function DuplicateDialog({ dup, onClose, navigate, onContinue, busy }) {
  return (
    <Dialog open={!!dup} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-md" data-testid="customer-duplicate-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" /> This customer may already exist
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[50vh] overflow-y-auto">
          {(dup?.candidates || []).map((c) => (
            <div key={c.id} className="rounded-lg border border-pawkt-border p-3" data-testid={`dup-candidate-${c.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-pawkt-text truncate">{c.name}</div>
                  <div className="text-xs text-pawkt-text-secondary flex items-center gap-1"><Phone className="h-3 w-3" />{c.phone || "—"}{c.email ? ` · ${c.email}` : ""}</div>
                  {!!(c.pets || []).length && (
                    <div className="text-xs text-pawkt-text-secondary mt-1 flex items-center gap-1"><PawPrint className="h-3 w-3" />{c.pets.filter(Boolean).join(", ")}</div>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => navigate(`/customers/${c.id}`)} data-testid={`dup-view-${c.id}`} className="min-h-[44px]">View</Button>
                  <Button size="sm" onClick={() => navigate(`/customers/${c.id}`)} data-testid={`dup-use-${c.id}`} className="min-h-[44px]">Use existing</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={onClose} className="min-h-[44px]">Cancel</Button>
          <Button onClick={onContinue} disabled={busy} data-testid="dup-continue-anyway" className="min-h-[44px] bg-slate-900 text-white">
            {busy ? "Creating…" : "Continue anyway"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
